// NodeTagsField.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './NodeTagsField.css';

// Icons
import Icon from 'svg/Icon';

// Components
import NodeTagsModal from 'node/form/fields/tags/NodeTagsModal';

/**
 * NodeTags
 * 
 * Displays the tags section for a node or a button to add tags if none exist.
 * 
 * @param {object} node - Node object.
 * @param {Function} onTagsSaved - Callback when tags is saved.
 *  
 *
 * @returns {ReactNode} - NodeTags component.
 */
const NodeTagsField = ({ node, onTagsSaved, readOnly = false }) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- STATE ----- //

    // Modal Open State

    const [tagsOpen, setTagsOpen] = useState(false);

    // Hover State
    const [hovered, setHovered] = useState(false);

    const tags = Array.isArray(node?.tags) ? node.tags : [];
    const hasTags = tags.length > 0;

    // ----- HANDLERS ----- //

    /**
     * handleOpen
     *
     * Opens the related surface.
     */
    const handleOpen = () => {
        if (readOnly) return;
        setTagsOpen(true);
    };

    /**
     * handleKeyDown
     *
     * Handles a key down event.
     *
     * @param {object} event - event.
     */
    const handleKeyDown = (event) => {
        if (readOnly) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setTagsOpen(true);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <div className="node-tags-field-container">

                {/* NODE TAGS FIELD */}
                <div
                  className="node-tags-field"
                  style={{
                    background: (!readOnly && hovered) ? 'var(--text-input-hover-bg, transparent)' : 'var(--text-input-bg, transparent)',
                    cursor: readOnly ? 'default' : 'pointer',
                  }}
                  role={readOnly ? undefined : 'button'}
                  tabIndex={readOnly ? undefined : 0}
                  onClick={handleOpen}
                  onKeyDown={handleKeyDown}
                  onMouseEnter={readOnly ? undefined : () => setHovered(true)}
                  onMouseLeave={readOnly ? undefined : () => setHovered(false)}
                >

                    {/* NODE TAGS ICON */}
                    <div className="node-tags-icon">
                        <Icon
                          iconKey="style"
                          color={theme.foregroundMuted}
                          width={22}
                          height={22}
                        />
                    </div>

                    {/* NODE TAGS GRID */}
                    <div className="node-tags-grid">
                        {hasTags ? (
                            tags.map((tag, index) => (
                                <span
                                  className="node-tags-pill"
                                  key={`${tag}-${index}`}
                                >
                                    {tag}
                                </span>
                            ))
                        ) : (
                            <span
                              className="node-tags-empty"
                            >
                                {readOnly ? '' : 'Add tags...'}
                            </span>
                        )}
                    </div>
                </div>

            </div>

            {!readOnly ? (
                <NodeTagsModal
                  node={node}
                  isOpen={tagsOpen}
                  setOpen={setTagsOpen}
                  onTagsSaved={onTagsSaved}
                />
            ) : null}
        </>
    );
};

// Dev Tools
NodeTagsField.displayName = 'NodeTagsField';

export default NodeTagsField;
